import React, { useEffect, useRef, useState } from "react";
import { Redirect } from "react-router-dom";
import { FormattedMessage, injectIntl } from "react-intl";
import * as Utils from "../../commons/utils/Utils";
import { Table, TableContainer, TableHead, TableRow, TableBody, Tooltip } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { StyledTableCellProps as StyledTableCell } from "../../commons/utils/TableStyles";
import Security from "../../commons/security/Security";
import GridFilterPanel from "../../commons/filters/GridFilterPanel.jsx";
import GridSearchResultsBadge from "../../commons/filters/GridSearchResultsBadge.jsx";
import GridColumnSelector from "../../commons/grid/GridColumnSelector.jsx";
import useColumnVisibility from "../../commons/grid/useColumnVisibility.js";
import { GridToolbar, GridToolbarButton } from "../../commons/grid/GridToolbar.jsx";
import LimpiarFiltros from "../../assets/images/limpiar-filtros.svg";
import { SECUENCIAS_FILTROS_STORAGE_KEY, buildSecuenciasFilterFields, filtrarSecuencias, hayFiltros } from "./secuenciasFilterFields.js";
import { SECUENCIAS_COLUMNAS_STORAGE_KEY, buildSecuenciasColumns } from "./secuenciasColumns.js";

const TipoFormularioSecuenciaGrid = (props) => {
  const [redirectTo, setRedirectTo] = useState(null);
  const [loading, setLoading] = useState(null);
  const [secuencias, setSecuencias] = useState(null);
  const [query, setQuery] = useState({});
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
  const [resetSignal, setResetSignal] = useState(false);
  const primerApply = useRef(true);
  // Columnas que el usuario eligió ver (se recuerda por usuario, por nombre de columna).
  const columnasGrilla = useColumnVisibility(buildSecuenciasColumns(), SECUENCIAS_COLUMNAS_STORAGE_KEY);
  const visibles = columnasGrilla.visibles;
  const cantidadVisibles = Object.keys(visibles).filter((nombre) => visibles[nombre]).length;

  useEffect(() => {
    setLoading(true);
    fetchSecuencias();
  }, []);

  const fetchSecuencias = () => {
    Utils.getData("/tipo-formulario-secuencia")
      .then((res) => {
        setSecuencias(res);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  const handleApply = (nuevaQuery) => {
    const completa = nuevaQuery || {};
    // Primer apply (el que hace el panel al montar con lo que tenía guardado): si hay filtros, se abre el panel para no dejar al usuario mirando un
    // listado filtrado sin ver por qué.
    if (primerApply.current && hayFiltros(completa)) setFiltrosAbiertos(true);
    primerApply.current = false;
    setQuery(completa);
  };

  // LIMPIAR LA BÚSQUEDA: la "✕" de la franja de resultados. Vuelve los filtros al default (incluido "Ver Inactivos") y no toca las columnas.
  const handleReset = () => {
    try {
      localStorage.removeItem(Security.claveDeUsuario(SECUENCIAS_FILTROS_STORAGE_KEY));
    } catch (e) {}
    setQuery({});
    setResetSignal(true);
  };

  // RESTAURAR: el botón del toolbar. Además de limpiar la búsqueda cierra el panel y vuelve a mostrar todas las columnas.
  const handleRestaurar = () => {
    setFiltrosAbiertos(false);
    columnasGrilla.mostrarTodas();
    handleReset();
  };

  // Son pocas filas por cliente: se filtra en el navegador.
  const filtradas = filtrarSecuencias(secuencias, query);
  const filtrosActivos = hayFiltros(query);
  const estadosDeOrigen = Array.from(new Set((secuencias || []).map((x) => x.movil_estado_origen))).filter(Boolean);
  const options = {
    estado: estadosDeOrigen.map((e) => ({ value: e, label: e })),
  };

  return (
    <>
      {redirectTo && <Redirect push to={redirectTo} />}
      <div className="card">
        <div className="card-content">
          <div className="card-body">
            <div className="container-fluid">
              {/* Los botones de la derecha (Restaurar y columnas) son más altos que la fila: sin este margen quedan pegados al encabezado de la tabla, a diferencia de tickets. */}
              <div className="row buttons-grid dt-icons" style={{ marginBottom: 25 }}>
                <div className="col d-flex">
                  {Security.hasPermission("CHECKLIST_CONFIGURAR_SECUENCIA") && (
                    <Tooltip title={props.intl.formatMessage({ id: "tipoFormularioSecuenciaGrid.button.label_add", defaultMessage: "Agregar" })} arrow placement="right">
                      <div className="btn btn-primary box-shadow-2 btn-round btn-lg btn-dt-main round-icon" onClick={() => setRedirectTo(props.match.url + "/add")}>
                        <i className="ft-plus"></i>
                      </div>
                    </Tooltip>
                  )}
                  <div
                    className="btn btn-grey-blue btn-round box-shadow-2 btn-dt-main round-icon"
                    onClick={() => setFiltrosAbiertos(!filtrosAbiertos)}
                    title={props.intl.formatMessage({ id: "Filtros", defaultMessage: "Filtros" })}
                  >
                    {filtrosAbiertos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </div>
                </div>
                <div className="col">
                  <GridSearchResultsBadge visible={filtrosActivos} mostrados={filtradas.length} total={secuencias ? secuencias.length : 0} onReset={handleReset} />
                </div>
                <div className="col">
                  <GridToolbar>
                    <GridToolbarButton
                      messageId="secuencias.tools_restore_filters.title_restaurar_filtros_y_columnas"
                      label="Restaurar filtros y columnas"
                      onClick={handleRestaurar}
                    >
                      <img id="filterReset" className="filter-reset" src={LimpiarFiltros} alt="" />
                    </GridToolbarButton>
                    <GridColumnSelector columns={columnasGrilla.seleccionables} esVisible={columnasGrilla.esVisible} onToggle={columnasGrilla.toggle} />
                  </GridToolbar>
                </div>
              </div>
            </div>

            <GridFilterPanel
              open={filtrosAbiertos}
              fields={buildSecuenciasFilterFields()}
              options={options}
              storageKey={SECUENCIAS_FILTROS_STORAGE_KEY}
              loading={loading}
              resetSignal={resetSignal}
              onResetSignalHandled={() => setResetSignal(false)}
              onApply={handleApply}
              applyOnMount
            />

            <TableContainer>
              <Table aria-label="tipo formulario secuencia table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="left">
                      <FormattedMessage id="tipoFormularioSecuenciaGrid.column_NOMBRE.label" defaultMessage="Nombre" />
                    </StyledTableCell>
                    <StyledTableCell show={visibles.aplicaA} align="left">
                      <FormattedMessage id="tipoFormularioSecuenciaGrid.column_APLICA_A.label" defaultMessage="Aplica a vehículos en estado" />
                    </StyledTableCell>
                    <StyledTableCell show={visibles.checklists} align="left">
                      <FormattedMessage id="tipoFormularioSecuenciaGrid.column_PASOS.label" defaultMessage="Checklists" />
                    </StyledTableCell>
                    <StyledTableCell show={visibles.intentos} align="left">
                      <FormattedMessage id="tipoFormularioSecuenciaGrid.column_INTENTOS.label" defaultMessage="Intentos máx." />
                    </StyledTableCell>
                    <StyledTableCell show={visibles.activo} align="left">
                      <FormattedMessage id="tipoFormularioSecuenciaGrid.column_ACTIVO.label" defaultMessage="Activo" />
                    </StyledTableCell>
                    <StyledTableCell align="left"></StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <StyledTableCell colSpan={cantidadVisibles} align="center">
                        <FormattedMessage id="Cargando" defaultMessage="Cargando" />
                      </StyledTableCell>
                    </TableRow>
                  )}
                  {!loading && secuencias && filtradas.length === 0 && (
                    <TableRow>
                      <StyledTableCell colSpan={cantidadVisibles} align="center">
                        <FormattedMessage id="tipoFormularioSecuenciaGrid.no_data_table" defaultMessage="No se encontraron secuencias configuradas..." />
                      </StyledTableCell>
                    </TableRow>
                  )}
                  {!loading &&
                    secuencias &&
                    filtradas.map((row) => (
                      <TableRow key={"tipoFormularioSecuenciaGridRow_" + row.id}>
                        <StyledTableCell align="left">{row.nombre}</StyledTableCell>
                        <StyledTableCell show={visibles.aplicaA} align="left">{row.movil_estado_origen}</StyledTableCell>
                        <StyledTableCell show={visibles.checklists} align="left">{row.items ? row.items.length : 0}</StyledTableCell>
                        <StyledTableCell show={visibles.intentos} align="left">{row.cantidad_maxima_intentos}</StyledTableCell>
                        <StyledTableCell show={visibles.activo} align="left">
                          {row.activo
                            ? props.intl.formatMessage({ id: "tipoFormularioSecuenciaGrid.activo.si", defaultMessage: "Si" })
                            : props.intl.formatMessage({ id: "tipoFormularioSecuenciaGrid.activo.no", defaultMessage: "No" })}
                        </StyledTableCell>
                        <StyledTableCell align="right" style={{ maxWidth: 50 }}>
                          <div className="d-flex justify-content-end">
                            <button
                              className="btn btn-sm btn-icon btn-dt-grid text-success p-0 mx-1"
                              onClick={() => setRedirectTo(props.match.url + "/" + row.id)}
                            >
                              <Tooltip
                                title={props.intl.formatMessage({ id: "tipoFormularioSecuenciaGrid.button.label_view", defaultMessage: "Ver" })}
                                arrow
                              >
                                <i className="fa fa-search fa-xs"></i>
                              </Tooltip>
                            </button>
                            {Security.hasPermission("CHECKLIST_CONFIGURAR_SECUENCIA") && (
                              <button
                                className="btn btn-sm btn-icon btn-dt-grid text-success p-0 mx-1"
                                onClick={() => setRedirectTo(props.match.url + "/" + row.id + "/edit")}
                              >
                                <Tooltip
                                  title={props.intl.formatMessage({ id: "tipoFormularioSecuenciaGrid.button.label_edit", defaultMessage: "Editar" })}
                                  arrow
                                >
                                  <i className="fa fa-pencil fa-xs"></i>
                                </Tooltip>
                              </button>
                            )}
                          </div>
                        </StyledTableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </div>
    </>
  );
};

export default injectIntl(TipoFormularioSecuenciaGrid);
